/**
 * Chem Duel question bank (v1).
 *
 * Target: 80 multiple-choice questions evenly split across four categories
 * (Bonding, Reactions, Organic chemistry, Lab safety). All authored for
 * Grades 9–12. Each question has a single correct answer and a one-sentence
 * explanation displayed after the answer reveals.
 *
 * Difficulty:
 *   1 = recall / definition
 *   2 = applied (one step of reasoning)
 *   3 = harder (multi-step or trickier distractors) - used for sudden death
 */

export type DuelCategory = "bonding" | "reactions" | "organic" | "safety";

export type DuelQuestion = {
  id: string;
  category: DuelCategory;
  difficulty: 1 | 2 | 3;
  prompt: string;
  choices: [string, string, string, string];
  /** 0..3 */
  correctIndex: 0 | 1 | 2 | 3;
  explanation: string;
};

export const CATEGORY_LABELS: Record<DuelCategory, string> = {
  bonding: "Bonding",
  reactions: "Reactions",
  organic: "Organic",
  safety: "Lab safety",
};

export const QUESTIONS: DuelQuestion[] = [
  // ============== BONDING (20) ==============
  {
    id: "b01",
    category: "bonding",
    difficulty: 1,
    prompt: "Which type of bond forms between a metal and a nonmetal?",
    choices: ["Covalent", "Ionic", "Metallic", "Hydrogen"],
    correctIndex: 1,
    explanation: "Metals lose electrons and nonmetals gain them, producing oppositely charged ions held by ionic bonds.",
  },
  {
    id: "b02",
    category: "bonding",
    difficulty: 1,
    prompt: "Covalent bonds form by:",
    choices: ["Transferring electrons", "Sharing electrons", "Losing protons", "Sharing protons"],
    correctIndex: 1,
    explanation: "Covalent bonds involve two atoms sharing one or more pairs of electrons.",
  },
  {
    id: "b03",
    category: "bonding",
    difficulty: 1,
    prompt: "How many valence electrons does oxygen have?",
    choices: ["4", "6", "8", "2"],
    correctIndex: 1,
    explanation: "Oxygen is in group 16, so it has 6 valence electrons.",
  },
  {
    id: "b04",
    category: "bonding",
    difficulty: 2,
    prompt: "Which molecule is polar?",
    choices: ["CO₂", "CH₄", "H₂O", "N₂"],
    correctIndex: 2,
    explanation: "Water is bent (~104.5°), so the O–H dipoles do not cancel - the molecule is polar.",
  },
  {
    id: "b05",
    category: "bonding",
    difficulty: 2,
    prompt: "Which has the highest electronegativity?",
    choices: ["Oxygen", "Fluorine", "Chlorine", "Nitrogen"],
    correctIndex: 1,
    explanation: "Fluorine is the most electronegative element (3.98 on the Pauling scale).",
  },
  {
    id: "b06",
    category: "bonding",
    difficulty: 1,
    prompt: "What charge does a sodium ion carry?",
    choices: ["−1", "+1", "+2", "−2"],
    correctIndex: 1,
    explanation: "Sodium loses one valence electron to form Na⁺.",
  },
  {
    id: "b07",
    category: "bonding",
    difficulty: 2,
    prompt: "What is the formula of magnesium chloride?",
    choices: ["MgCl", "MgCl₂", "Mg₂Cl", "Mg₂Cl₃"],
    correctIndex: 1,
    explanation: "Mg²⁺ needs two Cl⁻ ions to balance its +2 charge.",
  },
  {
    id: "b08",
    category: "bonding",
    difficulty: 2,
    prompt: "Metallic bonding is best described as:",
    choices: [
      "Shared pairs of electrons between two atoms",
      "A 'sea' of delocalized electrons around metal cations",
      "Electrostatic attraction between full positive and negative ions",
      "Weak attraction between temporary dipoles",
    ],
    correctIndex: 1,
    explanation: "In metals, valence electrons are delocalized over a lattice of cations, giving metals their conductivity and malleability.",
  },
  {
    id: "b09",
    category: "bonding",
    difficulty: 2,
    prompt: "Which intermolecular force is responsible for water's high boiling point?",
    choices: [
      "London dispersion",
      "Dipole–dipole",
      "Hydrogen bonding",
      "Ionic bonding",
    ],
    correctIndex: 2,
    explanation: "O–H bonds let water form strong hydrogen bonds between molecules, raising the boiling point.",
  },
  {
    id: "b10",
    category: "bonding",
    difficulty: 1,
    prompt: "Which compound is held together by ionic bonds?",
    choices: ["CO₂", "NaCl", "CH₄", "O₂"],
    correctIndex: 1,
    explanation: "NaCl is a metal+nonmetal pair forming Na⁺ and Cl⁻ ions in a lattice.",
  },
  {
    id: "b11",
    category: "bonding",
    difficulty: 2,
    prompt: "How many bonding pairs are between the carbons in ethyne (C₂H₂)?",
    choices: ["1", "2", "3", "4"],
    correctIndex: 2,
    explanation: "Ethyne has a carbon–carbon triple bond - three shared pairs.",
  },
  {
    id: "b12",
    category: "bonding",
    difficulty: 3,
    prompt: "Which molecule has a trigonal pyramidal shape?",
    choices: ["BF₃", "NH₃", "CH₄", "CO₂"],
    correctIndex: 1,
    explanation: "Ammonia has 3 bonding pairs + 1 lone pair on nitrogen, giving a trigonal pyramidal geometry.",
  },
  {
    id: "b13",
    category: "bonding",
    difficulty: 2,
    prompt: "What is the oxidation state of sulfur in SO₄²⁻?",
    choices: ["+2", "+4", "+6", "−2"],
    correctIndex: 2,
    explanation: "Each O is −2 (×4 = −8), and the ion charge is −2, so S must be +6.",
  },
  {
    id: "b14",
    category: "bonding",
    difficulty: 1,
    prompt: "Noble gases are unreactive because:",
    choices: [
      "They are gases at room temperature",
      "They have full valence shells",
      "They are radioactive",
      "They are too heavy",
    ],
    correctIndex: 1,
    explanation: "A complete outer shell of electrons makes noble gases very stable and unreactive.",
  },
  {
    id: "b15",
    category: "bonding",
    difficulty: 2,
    prompt: "Which best describes a coordinate (dative) covalent bond?",
    choices: [
      "Both electrons in the shared pair come from the same atom",
      "Electrons are completely transferred",
      "Three atoms share two electrons",
      "Electrons orbit between two nuclei equally",
    ],
    correctIndex: 0,
    explanation: "In a dative bond one atom donates both electrons of the shared pair (e.g., NH₄⁺).",
  },
  {
    id: "b16",
    category: "bonding",
    difficulty: 2,
    prompt: "Which compound contains a double bond?",
    choices: ["C₂H₆", "C₂H₄", "C₂H₂", "CH₄"],
    correctIndex: 1,
    explanation: "Ethene (C₂H₄) has a carbon–carbon double bond. Ethyne has a triple bond; ethane and methane have only single bonds.",
  },
  {
    id: "b17",
    category: "bonding",
    difficulty: 1,
    prompt: "An anion is an atom or group that has:",
    choices: ["Lost protons", "Gained electrons", "Lost electrons", "Gained protons"],
    correctIndex: 1,
    explanation: "Gaining electrons gives a net negative charge - that's an anion.",
  },
  {
    id: "b18",
    category: "bonding",
    difficulty: 2,
    prompt: "Which property is typical of ionic compounds?",
    choices: [
      "Low melting points",
      "Conduct electricity when molten or dissolved",
      "Soft and waxy solids",
      "Insoluble in all solvents",
    ],
    correctIndex: 1,
    explanation: "Ionic compounds conduct electricity when their ions are free to move (molten or in solution).",
  },
  {
    id: "b19",
    category: "bonding",
    difficulty: 3,
    prompt: "Rank the bonds by strength (strongest first): ionic, hydrogen bond, London dispersion.",
    choices: [
      "Hydrogen > Ionic > London",
      "Ionic > Hydrogen > London",
      "London > Hydrogen > Ionic",
      "Ionic > London > Hydrogen",
    ],
    correctIndex: 1,
    explanation: "Ionic bonds are full intramolecular bonds; hydrogen bonds are the strongest intermolecular force; London dispersion is the weakest.",
  },
  {
    id: "b20",
    category: "bonding",
    difficulty: 1,
    prompt: "What does VSEPR theory predict?",
    choices: [
      "The colour of a compound",
      "The shape of a molecule",
      "The boiling point",
      "The reaction rate",
    ],
    correctIndex: 1,
    explanation: "Valence Shell Electron Pair Repulsion theory predicts molecular geometry from electron-pair repulsion around the central atom.",
  },

  // ============== REACTIONS (20) ==============
  {
    id: "r01",
    category: "reactions",
    difficulty: 1,
    prompt: "What type of reaction is: 2H₂ + O₂ → 2H₂O ?",
    choices: ["Decomposition", "Synthesis (combination)", "Single displacement", "Combustion"],
    correctIndex: 1,
    explanation: "Two reactants combine to form a single product - that's a synthesis reaction. (It's also a combustion of hydrogen.)",
  },
  {
    id: "r02",
    category: "reactions",
    difficulty: 1,
    prompt: "Which is the correctly balanced equation for the combustion of methane?",
    choices: [
      "CH₄ + O₂ → CO₂ + H₂O",
      "CH₄ + 2O₂ → CO₂ + 2H₂O",
      "2CH₄ + O₂ → 2CO₂ + H₂O",
      "CH₄ + 3O₂ → CO₂ + 2H₂O",
    ],
    correctIndex: 1,
    explanation: "One CH₄ needs 2 O₂ to give 1 CO₂ and 2 H₂O - atoms balance on both sides.",
  },
  {
    id: "r03",
    category: "reactions",
    difficulty: 2,
    prompt: "What are the products when an acid reacts with a metal carbonate?",
    choices: [
      "Salt + hydrogen",
      "Salt + water",
      "Salt + water + carbon dioxide",
      "Salt + oxygen",
    ],
    correctIndex: 2,
    explanation: "Acid + carbonate → salt + water + CO₂ (e.g., HCl + CaCO₃ → CaCl₂ + H₂O + CO₂).",
  },
  {
    id: "r04",
    category: "reactions",
    difficulty: 2,
    prompt: "Predict the product: Zn + CuSO₄ → ?",
    choices: ["ZnO + CuS", "ZnSO₄ + Cu", "ZnCu + SO₄", "No reaction"],
    correctIndex: 1,
    explanation: "Zinc is more reactive than copper, so it displaces Cu²⁺ from solution: Zn + CuSO₄ → ZnSO₄ + Cu.",
  },
  {
    id: "r05",
    category: "reactions",
    difficulty: 1,
    prompt: "Which gas is produced when an acid reacts with a reactive metal?",
    choices: ["Oxygen", "Carbon dioxide", "Hydrogen", "Chlorine"],
    correctIndex: 2,
    explanation: "Acid + metal → salt + hydrogen gas (e.g., Mg + 2HCl → MgCl₂ + H₂).",
  },
  {
    id: "r06",
    category: "reactions",
    difficulty: 2,
    prompt: "Which describes oxidation in terms of electrons?",
    choices: ["Gain of electrons", "Loss of electrons", "Gain of protons", "Loss of neutrons"],
    correctIndex: 1,
    explanation: "OIL RIG: Oxidation Is Loss, Reduction Is Gain (of electrons).",
  },
  {
    id: "r07",
    category: "reactions",
    difficulty: 2,
    prompt: "In a neutralization reaction between HCl and NaOH, what is the salt produced?",
    choices: ["Sodium chloride", "Sodium hydroxide", "Hydrochloric acid", "Sodium hypochlorite"],
    correctIndex: 0,
    explanation: "HCl + NaOH → NaCl + H₂O. The salt is sodium chloride.",
  },
  {
    id: "r08",
    category: "reactions",
    difficulty: 1,
    prompt: "An exothermic reaction:",
    choices: [
      "Absorbs heat from the surroundings",
      "Releases heat to the surroundings",
      "Has no temperature change",
      "Only happens at low temperature",
    ],
    correctIndex: 1,
    explanation: "Exothermic reactions release energy as heat (ΔH is negative).",
  },
  {
    id: "r09",
    category: "reactions",
    difficulty: 2,
    prompt: "Which factor does NOT increase reaction rate?",
    choices: [
      "Higher temperature",
      "A catalyst",
      "Larger surface area",
      "Lower concentration of reactants",
    ],
    correctIndex: 3,
    explanation: "Lowering concentration reduces collision frequency and slows the reaction.",
  },
  {
    id: "r10",
    category: "reactions",
    difficulty: 2,
    prompt: "What does a catalyst do in a reaction?",
    choices: [
      "Increases the activation energy",
      "Provides an alternative pathway with lower activation energy",
      "Shifts the equilibrium to favour products",
      "Is consumed during the reaction",
    ],
    correctIndex: 1,
    explanation: "Catalysts speed reactions by lowering activation energy. They are not consumed and don't shift equilibrium position.",
  },
  {
    id: "r11",
    category: "reactions",
    difficulty: 3,
    prompt: "Balance: __ Al + __ O₂ → __ Al₂O₃. What are the coefficients?",
    choices: ["2, 3, 2", "4, 3, 2", "1, 1, 1", "4, 6, 2"],
    correctIndex: 1,
    explanation: "4 Al + 3 O₂ → 2 Al₂O₃ balances 4 Al and 6 O atoms on each side.",
  },
  {
    id: "r12",
    category: "reactions",
    difficulty: 2,
    prompt: "A precipitate forms when:",
    choices: [
      "Two solutions react to form a soluble product",
      "Two solutions react to form an insoluble solid",
      "A gas escapes from solution",
      "A solid dissolves in water",
    ],
    correctIndex: 1,
    explanation: "A precipitation reaction produces an insoluble solid that drops out of solution.",
  },
  {
    id: "r13",
    category: "reactions",
    difficulty: 2,
    prompt: "AgNO₃(aq) + NaCl(aq) → ?",
    choices: [
      "AgCl(s) + NaNO₃(aq)",
      "AgNa(s) + NO₃Cl(aq)",
      "No reaction",
      "Ag(s) + NaNO₃Cl(aq)",
    ],
    correctIndex: 0,
    explanation: "Silver chloride is insoluble - it precipitates while sodium nitrate stays dissolved.",
  },
  {
    id: "r14",
    category: "reactions",
    difficulty: 1,
    prompt: "Which is a single-displacement reaction?",
    choices: [
      "AB + CD → AD + CB",
      "A + B → AB",
      "AB → A + B",
      "A + BC → AC + B",
    ],
    correctIndex: 3,
    explanation: "Single displacement: one element replaces another in a compound (A + BC → AC + B).",
  },
  {
    id: "r15",
    category: "reactions",
    difficulty: 2,
    prompt: "What is the pH of a neutral solution at 25 °C?",
    choices: ["0", "7", "10", "14"],
    correctIndex: 1,
    explanation: "Pure water at 25 °C has [H⁺] = 1×10⁻⁷ M, giving pH 7.",
  },
  {
    id: "r16",
    category: "reactions",
    difficulty: 2,
    prompt: "Le Chatelier's principle says that a system at equilibrium will:",
    choices: [
      "Stop reacting once disturbed",
      "Shift to oppose the change applied",
      "Always shift to produce more product",
      "Increase its activation energy",
    ],
    correctIndex: 1,
    explanation: "When a stress is applied, equilibrium shifts in the direction that partially counteracts the change.",
  },
  {
    id: "r17",
    category: "reactions",
    difficulty: 3,
    prompt: "How many moles of O₂ are needed to react with 4 mol of H₂ to form water?",
    choices: ["1 mol", "2 mol", "4 mol", "8 mol"],
    correctIndex: 1,
    explanation: "From 2H₂ + O₂ → 2H₂O, the ratio is 2:1, so 4 mol H₂ needs 2 mol O₂.",
  },
  {
    id: "r18",
    category: "reactions",
    difficulty: 2,
    prompt: "Which is a strong acid?",
    choices: ["CH₃COOH", "H₂CO₃", "HCl", "NH₃"],
    correctIndex: 2,
    explanation: "HCl ionizes completely in water and is a strong acid. Acetic and carbonic acids are weak; ammonia is a weak base.",
  },
  {
    id: "r19",
    category: "reactions",
    difficulty: 1,
    prompt: "What does the (aq) state symbol mean?",
    choices: ["Anhydrous", "Aqueous (dissolved in water)", "A gas", "Aqua-based solid"],
    correctIndex: 1,
    explanation: "(aq) denotes a substance dissolved in water.",
  },
  {
    id: "r20",
    category: "reactions",
    difficulty: 2,
    prompt: "Which substance is reduced in the reaction: 2Mg + O₂ → 2MgO ?",
    choices: ["Magnesium", "Oxygen", "Both", "Neither"],
    correctIndex: 1,
    explanation: "Oxygen gains electrons (goes from 0 to −2), so it is reduced. Magnesium is oxidized.",
  },

  // ============== ORGANIC (20) ==============
  {
    id: "o01",
    category: "organic",
    difficulty: 1,
    prompt: "What is the general formula for an alkane?",
    choices: ["CₙH₂ₙ", "CₙH₂ₙ₊₂", "CₙH₂ₙ₋₂", "CₙHₙ"],
    correctIndex: 1,
    explanation: "Saturated, acyclic hydrocarbons follow CₙH₂ₙ₊₂.",
  },
  {
    id: "o02",
    category: "organic",
    difficulty: 1,
    prompt: "Which functional group is present in ethanol?",
    choices: ["Carbonyl", "Hydroxyl", "Carboxyl", "Amine"],
    correctIndex: 1,
    explanation: "Ethanol (CH₃CH₂OH) contains an –OH (hydroxyl) group, characteristic of alcohols.",
  },
  {
    id: "o03",
    category: "organic",
    difficulty: 2,
    prompt: "What is the IUPAC name of CH₃CH₂CH₂CH₃?",
    choices: ["Propane", "Butane", "Pentane", "Methane"],
    correctIndex: 1,
    explanation: "Four carbons in a straight chain with single bonds = butane.",
  },
  {
    id: "o04",
    category: "organic",
    difficulty: 1,
    prompt: "Which is an alkene?",
    choices: ["C₂H₆", "C₂H₄", "C₂H₂", "CH₄"],
    correctIndex: 1,
    explanation: "Alkenes have C=C double bonds. Ethene is C₂H₄.",
  },
  {
    id: "o05",
    category: "organic",
    difficulty: 2,
    prompt: "Which test distinguishes alkenes from alkanes?",
    choices: [
      "Lime water turns milky",
      "Bromine water decolourizes",
      "Litmus turns red",
      "Iodine in alcohol turns brown",
    ],
    correctIndex: 1,
    explanation: "Alkenes add Br₂ across the double bond, decolourizing brown bromine water. Alkanes do not react under the same conditions.",
  },
  {
    id: "o06",
    category: "organic",
    difficulty: 2,
    prompt: "What is the suffix for a carboxylic acid in IUPAC naming?",
    choices: ["-ol", "-al", "-oic acid", "-one"],
    correctIndex: 2,
    explanation: "Carboxylic acids use the suffix '-oic acid' (e.g., ethanoic acid).",
  },
  {
    id: "o07",
    category: "organic",
    difficulty: 2,
    prompt: "Which compound is a structural isomer of butane?",
    choices: ["Methane", "2-Methylpropane", "Pentane", "Cyclohexane"],
    correctIndex: 1,
    explanation: "2-Methylpropane has the same molecular formula (C₄H₁₀) as butane but a branched structure.",
  },
  {
    id: "o08",
    category: "organic",
    difficulty: 1,
    prompt: "What product forms when ethene reacts with hydrogen (with a catalyst)?",
    choices: ["Methane", "Ethane", "Ethyne", "Ethanol"],
    correctIndex: 1,
    explanation: "Hydrogenation adds H₂ across the C=C, converting ethene (C₂H₄) into ethane (C₂H₆).",
  },
  {
    id: "o09",
    category: "organic",
    difficulty: 2,
    prompt: "What is produced when a carboxylic acid reacts with an alcohol (acid catalyst)?",
    choices: ["An ester + water", "An aldehyde + water", "A ketone + CO₂", "An alkane + H₂"],
    correctIndex: 0,
    explanation: "Esterification: RCOOH + R'OH ⇌ RCOOR' + H₂O.",
  },
  {
    id: "o10",
    category: "organic",
    difficulty: 1,
    prompt: "Benzene's molecular formula is:",
    choices: ["C₆H₁₂", "C₆H₆", "C₅H₅", "C₆H₅OH"],
    correctIndex: 1,
    explanation: "Benzene is an aromatic ring with formula C₆H₆.",
  },
  {
    id: "o11",
    category: "organic",
    difficulty: 2,
    prompt: "Which functional group does methanal (HCHO) contain?",
    choices: ["Aldehyde", "Ketone", "Alcohol", "Ether"],
    correctIndex: 0,
    explanation: "An aldehyde has –CHO at the end of the chain. Methanal (formaldehyde) is the simplest aldehyde.",
  },
  {
    id: "o12",
    category: "organic",
    difficulty: 3,
    prompt: "Oxidising a primary alcohol gives first an aldehyde, then a:",
    choices: ["Ketone", "Carboxylic acid", "Ester", "Alkene"],
    correctIndex: 1,
    explanation: "Primary alcohol → aldehyde → carboxylic acid (e.g., ethanol → ethanal → ethanoic acid).",
  },
  {
    id: "o13",
    category: "organic",
    difficulty: 2,
    prompt: "What kind of reaction is alkene + Br₂ → dibromoalkane?",
    choices: ["Substitution", "Addition", "Elimination", "Combustion"],
    correctIndex: 1,
    explanation: "Both bromine atoms add across the double bond - that's an addition reaction.",
  },
  {
    id: "o14",
    category: "organic",
    difficulty: 2,
    prompt: "Which hydrocarbon is saturated?",
    choices: ["Ethene", "Ethyne", "Benzene", "Propane"],
    correctIndex: 3,
    explanation: "Saturated = only single C–C bonds. Propane fits; the others contain double, triple, or aromatic bonds.",
  },
  {
    id: "o15",
    category: "organic",
    difficulty: 3,
    prompt: "How many isomers does pentane (C₅H₁₂) have?",
    choices: ["1", "2", "3", "4"],
    correctIndex: 2,
    explanation: "Pentane, 2-methylbutane, and 2,2-dimethylpropane - three structural isomers.",
  },
  {
    id: "o16",
    category: "organic",
    difficulty: 1,
    prompt: "Polymerisation typically combines many small molecules called:",
    choices: ["Isomers", "Monomers", "Polymers", "Catalysts"],
    correctIndex: 1,
    explanation: "Monomers join end-to-end to form a polymer (e.g., ethene → poly(ethene)).",
  },
  {
    id: "o17",
    category: "organic",
    difficulty: 2,
    prompt: "What is the simplest amine?",
    choices: ["Methylamine", "Ammonia", "Methanamide", "Ethylamine"],
    correctIndex: 0,
    explanation: "Methylamine (CH₃NH₂) is the simplest amine. Ammonia (NH₃) is technically inorganic - no carbon.",
  },
  {
    id: "o18",
    category: "organic",
    difficulty: 1,
    prompt: "Crude oil is separated by:",
    choices: ["Filtration", "Fractional distillation", "Crystallisation", "Chromatography"],
    correctIndex: 1,
    explanation: "Fractional distillation separates crude oil into fractions by boiling-point range.",
  },
  {
    id: "o19",
    category: "organic",
    difficulty: 2,
    prompt: "Which best describes cracking?",
    choices: [
      "Joining short hydrocarbons into long chains",
      "Breaking long hydrocarbons into shorter, more useful molecules",
      "Burning hydrocarbons completely",
      "Adding oxygen to alkanes",
    ],
    correctIndex: 1,
    explanation: "Cracking uses heat and/or a catalyst to break long-chain alkanes into smaller alkanes and alkenes.",
  },
  {
    id: "o20",
    category: "organic",
    difficulty: 2,
    prompt: "Which compound is a ketone?",
    choices: ["CH₃CHO", "CH₃COCH₃", "CH₃COOH", "CH₃CH₂OH"],
    correctIndex: 1,
    explanation: "Propanone (CH₃COCH₃) has a carbonyl group between two carbon atoms - that's a ketone.",
  },

  // ============== LAB SAFETY (20) ==============
  {
    id: "s01",
    category: "safety",
    difficulty: 1,
    prompt: "What is the FIRST thing to do if you spill an acid on your skin?",
    choices: [
      "Cover it with a cloth",
      "Rinse with plenty of cold water",
      "Apply a strong base to neutralize",
      "Wait for it to dry",
    ],
    correctIndex: 1,
    explanation: "Flush the area with large amounts of cold water for at least several minutes - never neutralize on skin.",
  },
  {
    id: "s02",
    category: "safety",
    difficulty: 1,
    prompt: "When diluting concentrated acid, you should:",
    choices: [
      "Add water to the acid",
      "Add the acid slowly to water",
      "Mix them in any order",
      "Heat them first",
    ],
    correctIndex: 1,
    explanation: "Always Add Acid to water - adding water to acid can cause violent splashing because dilution is exothermic.",
  },
  {
    id: "s03",
    category: "safety",
    difficulty: 1,
    prompt: "Which PPE is required at all times in a school chemistry lab?",
    choices: ["Lab coat only", "Gloves only", "Safety goggles", "Earplugs"],
    correctIndex: 2,
    explanation: "Safety goggles protect the eyes from splashes and fragments and are mandatory whenever experiments are happening.",
  },
  {
    id: "s04",
    category: "safety",
    difficulty: 1,
    prompt: "Long hair in the lab should be:",
    choices: ["Left loose", "Tied back", "Cut short", "Covered with a hat"],
    correctIndex: 1,
    explanation: "Tie long hair back so it can't fall into flames, chemicals, or moving equipment.",
  },
  {
    id: "s05",
    category: "safety",
    difficulty: 2,
    prompt: "What does a safety data sheet (SDS) primarily provide?",
    choices: [
      "Pricing for chemicals",
      "Hazards, handling, and first-aid for a chemical",
      "Lab seating arrangements",
      "Lesson plans",
    ],
    correctIndex: 1,
    explanation: "An SDS lists hazards, safe handling, storage, PPE, and first-aid response for a chemical.",
  },
  {
    id: "s06",
    category: "safety",
    difficulty: 2,
    prompt: "You should NEVER pipette a chemical by:",
    choices: ["Using a pipette filler", "Using a bulb", "Using your mouth", "Using a syringe"],
    correctIndex: 2,
    explanation: "Mouth-pipetting is forbidden - always use a pipette filler or bulb.",
  },
  {
    id: "s07",
    category: "safety",
    difficulty: 1,
    prompt: "If a fire starts in a beaker, the safest immediate action is to:",
    choices: [
      "Pour water on it",
      "Cover it with a heat-proof mat or wet cloth",
      "Run away and tell someone later",
      "Blow on it",
    ],
    correctIndex: 1,
    explanation: "Smothering removes oxygen and puts out small fires safely. Water is dangerous on flammable liquids.",
  },
  {
    id: "s08",
    category: "safety",
    difficulty: 2,
    prompt: "Which gas requires a fume hood when generated?",
    choices: ["Oxygen", "Hydrogen", "Chlorine", "Nitrogen"],
    correctIndex: 2,
    explanation: "Chlorine is toxic and corrosive - it must be made and used in a working fume hood.",
  },
  {
    id: "s09",
    category: "safety",
    difficulty: 1,
    prompt: "An eye wash station should be used:",
    choices: [
      "Only after class",
      "Immediately if any chemical enters the eye, for at least 15 minutes",
      "Only if you wear contact lenses",
      "Only for acid splashes",
    ],
    correctIndex: 1,
    explanation: "Flush the eye continuously for at least 15 minutes after any chemical exposure, then seek medical help.",
  },
  {
    id: "s10",
    category: "safety",
    difficulty: 1,
    prompt: "Broken glass in the lab should be:",
    choices: [
      "Put in the regular bin",
      "Picked up by hand",
      "Placed in a designated broken-glass container",
      "Left for the cleaner",
    ],
    correctIndex: 2,
    explanation: "Broken glass goes in a labelled, puncture-resistant container - never the regular waste bin.",
  },
  {
    id: "s11",
    category: "safety",
    difficulty: 2,
    prompt: "When heating a test tube, the opening should point:",
    choices: [
      "Toward yourself",
      "Toward the nearest person",
      "Away from yourself and others",
      "Straight up",
    ],
    correctIndex: 2,
    explanation: "Hot liquids can spit. Always angle the opening into empty space, away from people.",
  },
  {
    id: "s12",
    category: "safety",
    difficulty: 1,
    prompt: "What does the GHS pictogram of a flame indicate?",
    choices: ["Toxic", "Flammable", "Corrosive", "Oxidising"],
    correctIndex: 1,
    explanation: "The flame pictogram on a chemical label means flammable - keep away from heat, sparks, and open flames.",
  },
  {
    id: "s13",
    category: "safety",
    difficulty: 1,
    prompt: "Eating or drinking in the lab is:",
    choices: ["Allowed if it's water", "Allowed before experiments", "Strictly prohibited", "Allowed for staff only"],
    correctIndex: 2,
    explanation: "Food, drink, and gum can be contaminated by chemicals or fumes - banned in all chemistry labs.",
  },
  {
    id: "s14",
    category: "safety",
    difficulty: 2,
    prompt: "Waste organic solvents should be:",
    choices: [
      "Poured down the drain",
      "Evaporated in the room",
      "Placed in a labelled organic-waste container",
      "Mixed with acid waste",
    ],
    correctIndex: 2,
    explanation: "Organic solvents must go in a clearly labelled, separate waste container for proper disposal.",
  },
  {
    id: "s15",
    category: "safety",
    difficulty: 2,
    prompt: "If you smell an unfamiliar chemical, you should:",
    choices: [
      "Inhale deeply to identify it",
      "Waft a small amount toward your nose with your hand",
      "Put your nose directly over the bottle",
      "Ask a friend to smell it first",
    ],
    correctIndex: 1,
    explanation: "Wafting brings only a small, safe amount of vapour toward you. Never inhale directly.",
  },
  {
    id: "s16",
    category: "safety",
    difficulty: 2,
    prompt: "What type of fire extinguisher is appropriate for an electrical fire?",
    choices: ["Water", "CO₂ or dry powder", "Foam", "Wet chemical"],
    correctIndex: 1,
    explanation: "CO₂ and dry powder extinguishers don't conduct electricity, making them safe for electrical fires.",
  },
  {
    id: "s17",
    category: "safety",
    difficulty: 1,
    prompt: "Loose clothing or open-toed shoes in a lab are:",
    choices: ["Recommended", "Not allowed", "Allowed in summer", "Allowed during clean-up"],
    correctIndex: 1,
    explanation: "Closed-toe shoes and fitted clothing reduce the risk of spills and contact with hot or sharp items.",
  },
  {
    id: "s18",
    category: "safety",
    difficulty: 3,
    prompt: "Mixing bleach (NaClO) with ammonia (NH₃) is dangerous because it produces:",
    choices: ["Oxygen", "Toxic chloramine vapours", "Hydrogen", "Salt and water"],
    correctIndex: 1,
    explanation: "Bleach + ammonia releases chloramine gases - irritating, toxic, and potentially deadly in enclosed spaces.",
  },
  {
    id: "s19",
    category: "safety",
    difficulty: 1,
    prompt: "When you finish an experiment, you should:",
    choices: [
      "Leave equipment as it is",
      "Wash hands, clean station, and dispose of waste correctly",
      "Take chemicals home to study",
      "Turn off the lights only",
    ],
    correctIndex: 1,
    explanation: "Clean your workspace, dispose of waste in proper containers, and wash your hands before leaving.",
  },
  {
    id: "s20",
    category: "safety",
    difficulty: 2,
    prompt: "Which symbol indicates a corrosive substance on a chemical label (GHS)?",
    choices: [
      "A flame",
      "A skull and crossbones",
      "A hand and surface being eaten by drops",
      "An exclamation mark",
    ],
    correctIndex: 2,
    explanation: "The corrosive pictogram shows liquid drops eating into a hand and a surface.",
  },
];

/** Number of questions per category - useful for sanity checks. */
export const QUESTION_COUNT: Record<DuelCategory, number> = QUESTIONS.reduce(
  (acc, q) => {
    acc[q.category] = (acc[q.category] ?? 0) + 1;
    return acc;
  },
  { bonding: 0, reactions: 0, organic: 0, safety: 0 } as Record<DuelCategory, number>,
);